import React, { useEffect, useState, useCallback } from "react";
import { UserCog, Copy, ShieldCheck, Users, Save } from "lucide-react";
import AdvisorLayout from "../components/layout/AdvisorLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function AdvisorProfile() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ display_name: "", firm_name: "", bio: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pfa/me");
      setProfile(data);
      setForm({
        display_name: data.display_name || "",
        firm_name: data.firm_name || "",
        bio: data.bio || "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/pfa/me", form);
      setProfile((p) => ({ ...p, ...data }));
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyCode = () => {
    if (!profile?.advisor_code) return;
    navigator.clipboard?.writeText(profile.advisor_code);
    toast.success("Advisor code copied");
  };

  return (
    <AdvisorLayout>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-600 text-white">
            <UserCog className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
              My Profile
            </h1>
            <p className="text-sm text-slate-500">
              This is what clients see when they connect with you.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-6">
            {/* CODE + STATS */}
            <div className="rounded-3xl bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-500/20 p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                <ShieldCheck className="h-4 w-4" />
                Permanent advisor code
              </div>
              <div className="mt-2 flex items-center gap-3">
                <span className="font-mono text-2xl font-bold text-slate-900 dark:text-white">
                  {profile?.advisor_code || "—"}
                </span>
                <button
                  onClick={copyCode}
                  className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                <Users className="h-4 w-4 text-emerald-500" />
                {profile?.client_count || 0} connected client
                {profile?.client_count === 1 ? "" : "s"}
              </div>
            </div>

            {/* EDITABLE */}
            <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 space-y-4">
              <Field
                label="Display name"
                value={form.display_name}
                onChange={(v) => setForm((f) => ({ ...f, display_name: v }))}
                placeholder="How clients see you"
              />
              <Field
                label="Firm / practice name"
                value={form.firm_name}
                onChange={(v) => setForm((f) => ({ ...f, firm_name: v }))}
                placeholder="Optional"
              />
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Bio
                </label>
                <textarea
                  rows={4}
                  value={form.bio}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, bio: e.target.value }))
                  }
                  className="input-soft"
                  placeholder="A short note clients see in the connection screen."
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdvisorLayout>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
        {label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input-soft"
      />
    </div>
  );
}
